import { selectCurrentTeam, selectIsActionsBlocked, showNotifier } from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FC, createContext, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCreateInvoiceMutation, useLazyGetCustomerByIdQuery } from '../api';
import { PreviewBar } from '../components';
import { InvoicePayload, TaxesAndFeesResponse } from '../invoices.model';
import {
  addInvoiceValidationError,
  enableCustomerNotSelectedError,
  initialiseInvoicePaymentModes,
  resetInvoiceState,
  selectHasEstimateRelatedJob,
  selectInvoice,
  selectInvoiceFromEstimateNumber,
  selectInvoiceFromEstimateStatus,
  selectInvoiceFromJobNumber,
  selectIsInitialisingSliceData,
  selectIsInvoiceFromEstimate,
  selectIsInvoiceFromJob,
  selectIsSignatureEnabled,
  updateInvoiceCreatedResponse,
  updateInvoiceCustomer,
  updateIsSignatureEnabled // Import the action
} from '../InvoicesSlice';
import InvoiceOptionsSection from './components/InvoiceOptionsSection';
import InvoicesTabSideNav from './components/InvoicesTabSideNav';
import LineItemsSection from './components/LineItemsSection';
import NotesAttachmentsSection from './components/NotesAttachmentsSection';
import PaymentOptionsSection from './components/PaymentOptionsSection';
import { InvoiceTabEnum, minimumLineItemsError } from './constants';

import routesPath from 'routes/RoutesPath';
import { InvoicesRouteState } from 'routes/types';
import { NotifierTypes } from 'constants/notifierConstants';
import queryString from 'query-string';
import { constructArrayObjectAsString, throttle } from 'utils/commonUtils';
import { ErrorResponse } from 'containers/app/app.model';
import { logAnalyticEvent } from 'utils/analytics';
import { CleverTapEventsInvoices } from '../events';
import {
  checkIfAnyPaymentModeIsEligible,
  getAttachmentArray,
  getIsSignatureEnabledState,
  getLineItemsArrayForEvent,
  getServiceChargeEventPayload,
  hasChangesToSync,
  prepareDiscountRelatedPayload,
  prepareLineItemList,
  prepareServiceChargeListRequestPayload,
  prepareSummatedUniqueTaxList
} from '../utils';
import { InvoiceSourceEnum, InvoiceVersion } from '../constants';
import { API_SOURCE, DispatchDestination, invoiceMinimumAmount } from 'constants/common';
import { useGetItemSuggestionsAndGlobalTaxesAndFeesQuery } from 'containers/product-catalog/api';
import SignatureOptionsSection from './components/SignatureOptionsSection';
import { useGetMerchantCustomizationsQuery } from 'containers/settings/api';
import { FeatureToggleState } from 'containers/settings/constants';
import { useCreateInvoiceFromJobMutation, useLazyGetJobByIdQuery } from 'containers/jobs/api';
import CreateInvoiceCustomer from './components/CreateInvoiceCustomer';
import { GenericModal, Loader } from 'components';
import { CrossIconvector } from 'assets/icons';
import NoSelfCheckoutPopup from '../components/NoSelfCheckoutPopup';
import InvoiceErrorsPopup from '../components/InvoiceErrorsPopup';
import { InvoiceErrorFieldsDefault } from './types';
import { updateCustomizations } from 'containers/settings/invoiceSettingsSlice';
import { useLazyGetEstimateByIdQuery } from 'containers/estimates/api';
import { EstimateStatus } from 'containers/estimates/constants';
import BlockedInfoPopup from 'containers/home/components/BlockedInfoPopup';
import { logUserNetworkData } from 'utils/analytics/utils';

interface ICreateInvoiceContext {
  selectedInvoiceTab: InvoiceTabEnum;
  setSelectedInvoiceTab: React.Dispatch<React.SetStateAction<InvoiceTabEnum>>;
}

interface CreateInvoiceProps {
  onPreviewClick: () => void;
  taxesAndFeesData: TaxesAndFeesResponse;
  isLoadingTaxesAndFees: boolean;
}

export const CreateInvoiceContext = createContext<ICreateInvoiceContext>({
  selectedInvoiceTab: InvoiceTabEnum.BILL_TO,
  setSelectedInvoiceTab: null
});

const CreateInvoice: FC<CreateInvoiceProps> = ({ onPreviewClick, taxesAndFeesData, isLoadingTaxesAndFees }) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isActionsBlocked = useAppSelector(selectIsActionsBlocked);
  const selectedInvoice = useAppSelector(selectInvoice);
  const selectedIsSignatureEnabled = useAppSelector(selectIsSignatureEnabled);
  const isInvoiceFromJob = useAppSelector(selectIsInvoiceFromJob);
  const invoiceFromJobNumber = useAppSelector(selectInvoiceFromJobNumber);
  const invoiceFromEstimateNumber = useAppSelector(selectInvoiceFromEstimateNumber);
  const isInitialising = useAppSelector(selectIsInitialisingSliceData);
  const isInvoiceFromEstimate = useAppSelector(selectIsInvoiceFromEstimate);
  const estimateStatus = useAppSelector(selectInvoiceFromEstimateStatus);
  const hasEstimateRelatedJob = useAppSelector(selectHasEstimateRelatedJob);
  const isEstimatePending = estimateStatus?.toUpperCase() === EstimateStatus.PENDING;

  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [showSyncConfirmationPopup, setShowSyncConfirmationPopup] = useState(false);
  const [showNoSelfCheckoutPopup, setShowNoSelfCheckoutPopup] = useState(false);
  const [showInvoiceErrorsPopup, setShowInvoiceErrorsPopup] = useState({
    open: false,
    errors: InvoiceErrorFieldsDefault
  });
  const [showMerchantBlockedPopup, setShowMerchantBlockedPopup] = useState(false);

  useGetItemSuggestionsAndGlobalTaxesAndFeesQuery({
    teamId: currentTeam.id,
    profession: currentTeam.profession,
    dispatchDestination: DispatchDestination.INVOICE_SLICE
  });

  const {
    data: merchantCustomisations,
    isSuccess: merchantCustomisationsSuccess,
    error: merchantCustomizationsError,
    isError: isMerchantCustomizationsError
  } = useGetMerchantCustomizationsQuery({
    teamId: currentTeam.id.toString()
  });

  useEffect(() => {
    if (merchantCustomisationsSuccess) {
      dispatch(updateIsSignatureEnabled(getIsSignatureEnabledState(merchantCustomisations.customerSignatureConfig)));
      dispatch(updateCustomizations(merchantCustomisations));
      dispatch(initialiseInvoicePaymentModes(merchantCustomisations.paymentModes));
    }
  }, [merchantCustomisationsSuccess]);

  useEffect(() => {
    if (merchantCustomizationsError) {
      logAnalyticEvent('webapp_payment_settings_error', {
        errorCode: merchantCustomizationsError.code,
        errorMessage: JSON.stringify(merchantCustomizationsError.message),
        errorName: merchantCustomizationsError.name
      });
      logUserNetworkData();
    }
  }, [merchantCustomizationsError, isMerchantCustomizationsError]);

  const [
    createInvoiceMutation,
    {
      isSuccess: isCreateInvoiceSuccess,
      data: invoiceCreatedResponse,
      isError: isCreateInvoiceError,
      error: createInvoiceError,
      isLoading: isCreateInvoiceLoading
    }
  ] = useCreateInvoiceMutation();
  const [
    createInvoiceFromJobMutation,
    {
      isSuccess: isCreateInvoiceFromJobSuccess,
      data: invoiceFromJobCreatedResponse,
      isError: isCreateInvoiceFromJobError,
      error: createInvoiceFromJobError,
      isLoading: isCreateInvoiceFromJobLoading
    }
  ] = useCreateInvoiceFromJobMutation();

  const [selectedInvoiceTab, setSelectedInvoiceTab] = useState(InvoiceTabEnum.BILL_TO);

  const queryParams = new URLSearchParams(window.location.search);

  const urlQuery = queryString.parse(location.search);
  const jobId = queryParams.get('jobId');
  const estimateId = queryParams.get('estimateId');
  const [getCustomerByIdQuery, getCustomerByIdState] = useLazyGetCustomerByIdQuery();
  const { data: customerData, isSuccess: customerDataSuccess } = getCustomerByIdState;
  const [getJobByIdQuery, { isFetching: isJobByIdFetching }] = useLazyGetJobByIdQuery();

  const [getEstimateByIdQuery, { isFetching: isEstimateByIdFetching }] = useLazyGetEstimateByIdQuery();

  useEffect(() => {
    if (urlQuery.customerId) {
      const { customerId } = urlQuery;
      getCustomerByIdQuery({ teamId: currentTeam.id, customerId: customerId as string });
    }
  }, []);

  useEffect(() => {
    if (jobId && !isLoadingTaxesAndFees) {
      getJobByIdQuery({ teamId: currentTeam.id, jobId: jobId, isFromInvoice: true });
    }
  }, [jobId, currentTeam.id, isLoadingTaxesAndFees]);

  useEffect(() => {
    if (estimateId && !isLoadingTaxesAndFees) {
      getEstimateByIdQuery({ teamId: currentTeam.id, estimateId, isFromInvoice: true });
    }
  }, [estimateId, currentTeam.id]);

  useEffect(() => {
    if (isInvoiceFromJob && !isInitialising) {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: 'Customer and job details copied to invoice',
            isMessageHtml: false
          },
          showClose: true,
          type: NotifierTypes.SUCCESS
        })
      );
    }
  }, [isInitialising, isInvoiceFromJob]);

  useEffect(() => {
    if (customerDataSuccess) {
      dispatch(updateInvoiceCustomer({ ...customerData, id: null, ID: customerData?.id }));
    }
  }, [customerDataSuccess]);

  useEffect(() => {
    if (isCreateInvoiceSuccess || isCreateInvoiceFromJobSuccess) {
      dispatch(resetInvoiceState());
      dispatch(
        updateInvoiceCreatedResponse(
          isCreateInvoiceSuccess ? invoiceCreatedResponse : invoiceFromJobCreatedResponse.data
        )
      );
      if (location.state?.returnTo) {
        navigate(location.state.returnTo, {
          state: location.state.returnToState || null,
          replace: true
        });
        dispatch(
          showNotifier({
            message: {
              primaryMessage: 'Invoice created successfully!',
              secondaryMessage: '',
              isMessageHtml: false
            },
            type: NotifierTypes.SUCCESS,
            showClose: true
          })
        );
      } else {
        const invoiceRouteState: InvoicesRouteState = {
          showShareModal: true,
          isEditInvoiceShare: false,
          showSidePanel: false
        };
        navigate(routesPath.INVOICES, { replace: true, state: invoiceRouteState });
      }
    }
  }, [isCreateInvoiceSuccess, isCreateInvoiceFromJobSuccess]);

  useEffect(() => {
    if (isCreateInvoiceError || isCreateInvoiceFromJobError) {
      const error = isCreateInvoiceError
        ? (createInvoiceError as ErrorResponse)
        : (createInvoiceFromJobError as ErrorResponse);
      if (error?.data?.details) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: error.data.details,
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      } else {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: 'Something went wrong',
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      }
    }
  }, [isCreateInvoiceError, isCreateInvoiceFromJobError]);

  const handleScrollToTab = (invoiceTab: InvoiceTabEnum) => {
    const tabElem = document.getElementById(invoiceTab);
    if (tabElem) {
      tabElem.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
    }
  };

  useEffect(() => {
    handleScrollToTab(selectedInvoiceTab);
  }, [selectedInvoiceTab]);

  const onPreviewButtonClick = () => {
    let isPageIncomplete = false;
    if (selectedInvoice.invoiceLineItems.length === 0) {
      isPageIncomplete = true;
      dispatch(addInvoiceValidationError(minimumLineItemsError));
    }
    if (selectedInvoice.invoiceCustomer === null) {
      isPageIncomplete = true;
      dispatch(enableCustomerNotSelectedError());
    }

    if (isPageIncomplete) {
      return;
    }

    onPreviewClick();
  };

  const hasDataChanges = () => {
    return hasChangesToSync(
      selectedInvoice.invoiceCustomer,
      selectedInvoice.initialInvoiceCustomer,
      selectedInvoice.invoiceLineItems,
      selectedInvoice.initialInvoiceLineItems,
      selectedInvoice.selectedServiceCharge,
      selectedInvoice.initialServiceCharge,
      selectedInvoice.invoiceSubTotal,
      selectedInvoice.initialInvoiceSubTotal,
      selectedInvoice.isServiceFeeEnabled,
      selectedInvoice.isServiceFeeEnabledInitial,
      selectedInvoice.currentSurcharge,
      selectedInvoice.initialSurcharge
    );
  };

  const onCreateAndShare = () => {
    if (isActionsBlocked) {
      setShowMerchantBlockedPopup(true);
      return;
    }
    let isPageIncomplete = false;
    let errors = InvoiceErrorFieldsDefault;
    if (selectedInvoice.invoiceLineItems.length === 0) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }
    if (selectedInvoice.invoiceCustomer === null) {
      isPageIncomplete = true;
      errors = { ...errors, customer: true };
    }
    const invoiceMinAmount = +currentTeam?.minimumAmount || invoiceMinimumAmount;
    if (selectedInvoice.invoiceSubTotal < invoiceMinAmount) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }
    const isSelfCheckoutEmpty =
      selectedInvoice.invoicePaymentModes.filter(item => item.isSelected && item.teamLevelVisiblity).length === 0;
    if (isSelfCheckoutEmpty) {
      const isAnyPaymentModeEligible = checkIfAnyPaymentModeIsEligible(
        merchantCustomisations.paymentModes,
        selectedInvoice.finalAmount
      );
      if (!isAnyPaymentModeEligible) {
        isPageIncomplete = true;
        errors = { ...errors, paymentMethods: true };
      }
    }

    if (isPageIncomplete) {
      setShowInvoiceErrorsPopup({ open: true, errors });
      return;
    }

    if (isSelfCheckoutEmpty) {
      setShowNoSelfCheckoutPopup(true);
      return;
    }

    const showSyncConfirmation = (isInvoiceFromJob || (isInvoiceFromEstimate && isEstimatePending)) && hasDataChanges();

    if (showSyncConfirmation) {
      setShowSyncConfirmationPopup(true);
    } else if (isInvoiceFromEstimate || isInvoiceFromJob) {
      handleCreateInvoiceFromOtherEntities();
    } else {
      handleCreateInvoice();
    }
  };

  const handleCreateInvoiceFromOtherEntities = () => {
    if (isInvoiceFromJob) {
      createInvoiceFromJobMutation({
        teamId: currentTeam.id,
        jobId: jobId,
        body: { jobInvoiceRequest: getCreateInvoicePayload(), jobNumber: invoiceFromJobNumber, source: API_SOURCE.WEB }
      });
    } else {
      handleCreateInvoice();
    }
  };

  const handleCreateInvoice = () => {
    createInvoiceMutation(getCreateInvoicePayload());
  };

  const handleOnCreateInvoiceWithoutSelfCheckout = () => {
    const showSyncConfirmation = (isInvoiceFromJob || (isInvoiceFromEstimate && isEstimatePending)) && hasDataChanges();
    if (showSyncConfirmation) {
      setShowSyncConfirmationPopup(true);
    } else if (isInvoiceFromEstimate || isInvoiceFromJob) {
      handleCreateInvoiceFromOtherEntities();
    } else {
      handleCreateInvoice();
    }
  };

  const handleOnEditPaymentMethods = () => {
    handleCloseNoSelfCheckoutPopup();
    handleScrollToTab(InvoiceTabEnum.PAYMENT_OPTIONS);
  };

  const handleCloseNoSelfCheckoutPopup = () => {
    setShowNoSelfCheckoutPopup(false);
  };

  const handleCloseInvoiceErrorsPopup = () => {
    setShowInvoiceErrorsPopup({ open: false, errors: InvoiceErrorFieldsDefault });
  };

  const getPaymentModes = () => {
    const selectedModes = selectedInvoice.invoicePaymentModes.filter(item => item.isSelected).map(item => item.id);

    const updatedPaymentModes = merchantCustomisations.paymentModes.map(paymentMode => {
      if (paymentMode.teamLevelVisiblity) {
        if (selectedModes.includes(paymentMode.id)) {
          return {
            id: paymentMode.id,
            enabled: true
          };
        }
        return {
          id: paymentMode.id,
          enabled: false
        };
      }
      return {
        id: paymentMode.id,
        enabled: paymentMode.enabled
      };
    });
    return updatedPaymentModes;
  };

  const getCreateInvoicePayload = () => {
    const serviceChargeListPayload = prepareServiceChargeListRequestPayload(selectedInvoice.selectedServiceCharge);

    const invoicePayload: InvoicePayload = {
      version: InvoiceVersion.THREE_POINT_ZERO,
      teamId: currentTeam.id.toString(),
      storeName: currentTeam.storeName,
      // store all level amounts in redux slice eg: subtotal, finalamount etc
      amount: selectedInvoice.invoiceSubTotal.toString(),
      comment: selectedInvoice.comment,
      customerId: selectedInvoice?.invoiceCustomer?.ID,
      lineItems: prepareLineItemList(selectedInvoice.invoiceLineItems),
      ...prepareDiscountRelatedPayload(selectedInvoice.discountFormData),
      surchargePercentage: selectedInvoice.isServiceFeeEnabled
        ? selectedInvoice.currentSurcharge.percentageWithBps ?? '0'
        : '0',
      isServiceFeeEnabled: selectedInvoice.isServiceFeeEnabled,
      attachments: getAttachmentArray(selectedInvoice),
      tipEnabled: selectedInvoice.tipEnabled,
      tncId: taxesAndFeesData.tnc.enabled ? taxesAndFeesData.tnc.tncId : 0,
      ...prepareSummatedUniqueTaxList({
        appliedLineItemLevelTaxes: selectedInvoice.appliedLineItemLevelTaxes,
        invoiceLineItems: selectedInvoice.invoiceLineItems,
        selectedServiceCharge: selectedInvoice.selectedServiceCharge
      }),
      serviceCharges: serviceChargeListPayload,
      serviceChargeAmount: serviceChargeListPayload?.length > 0 ? serviceChargeListPayload[0].totalAmount : '0',
      source: InvoiceSourceEnum.WEB,
      isSignatureEnabled: selectedIsSignatureEnabled,
      serviceAddressId: selectedInvoice.invoiceCustomer?.serviceAddress?.[0]?.id ?? null,
      paymentModes: getPaymentModes(),
      ...(estimateId
        ? { relativeEntity: { entityType: 'ESTIMATE', entityId: estimateId, entityNumber: invoiceFromEstimateNumber } }
        : {})
    };

    const serviceChargeEventPayload = getServiceChargeEventPayload(serviceChargeListPayload);
    logAnalyticEvent(CleverTapEventsInvoices.webInvoicesCreateInvoiceSave, {
      customerName: selectedInvoice?.invoiceCustomer?.name,
      customerId: selectedInvoice?.invoiceCustomer?.ID,
      teamId: currentTeam.id.toString(),
      storeName: currentTeam.storeName,
      amount: selectedInvoice.invoiceSubTotal.toString(),
      'Line Items': constructArrayObjectAsString(
        getLineItemsArrayForEvent(selectedInvoice.invoiceLineItems),
        'Line item'
      ),
      ...prepareDiscountRelatedPayload(selectedInvoice.discountFormData),
      surchargePercentage: selectedInvoice.isServiceFeeEnabled
        ? selectedInvoice.currentSurcharge.percentageWithBps ?? '0'
        : '0',
      ...(serviceChargeEventPayload?.length > 0 ? { serviceCharges: serviceChargeEventPayload } : {}),
      serviceAddressId: selectedInvoice.invoiceCustomer?.serviceAddress?.[0]?.id ?? ''
    });
    return invoicePayload;
  };

  const createAndShareHandler = useCallback(throttle(onCreateAndShare, 800), [
    selectedInvoice,
    isInvoiceFromJob,
    isInvoiceFromEstimate,
    isActionsBlocked
  ]);

  const onBackIconClick = () => {
    navigate(-1);
    dispatch(resetInvoiceState());
  };

  const handleSignatureStateChange = (value: boolean) => {
    dispatch(updateIsSignatureEnabled(value));
  };

  const checkIsSignatureDisabled = () => {
    if (merchantCustomisationsSuccess) {
      return merchantCustomisations.customerSignatureConfig === FeatureToggleState.ALWAYS_ON;
    }
    return false;
  };

  const getSyncPopupTitleAndSubtitle = (type: 'title' | 'subtitle') => {
    const title = `Save and sync changes to the related ${isInvoiceFromJob || hasEstimateRelatedJob ? 'job' : ''} ${
      (hasEstimateRelatedJob || isInvoiceFromJob) && (isInvoiceFromEstimate || isEstimatePending) ? 'and' : ''
    } ${isInvoiceFromEstimate || isEstimatePending ? 'estimate' : ''}?`;
    const subtitle = `Changes in your invoices will be applied to the associated ${
      isInvoiceFromJob || hasEstimateRelatedJob ? `job ${invoiceFromJobNumber}` : ''
    } ${(hasEstimateRelatedJob || isInvoiceFromJob) && (isInvoiceFromEstimate || isEstimatePending) ? 'and' : ''} ${
      isInvoiceFromEstimate || isEstimatePending ? `estimate ${invoiceFromEstimateNumber}` : ''
    } as well.`;
    return type === 'title' ? title : subtitle;
  };

  return (
    <CreateInvoiceContext.Provider value={{ selectedInvoiceTab, setSelectedInvoiceTab }}>
      {isInitialising || isJobByIdFetching || isEstimateByIdFetching || isLoadingTaxesAndFees ? (
        <Loader />
      ) : (
        <div className="flex h-full w-full flex-col border">
          <PreviewBar
            className="border border-borderGray"
            title="New Invoice"
            onPrimaryClick={createAndShareHandler}
            onReturn={onBackIconClick}
            primaryBtnTitle="Create & Share"
            disablePrimaryBtn={false}
            secondaryBtnTitle="Preview"
            onSecondaryClick={onPreviewButtonClick}
            primaryBtnId="create-and-share-invoice-button"
            secondaryBtnId="create-invoice-preview-button"
          />
          <div className="flex h-full w-full flex-row gap-x-[120px] overflow-hidden bg-primary">
            <div
              className="hidden w-1/3 max-w-[31.25rem] flex-col items-center p-11 pr-0
          lg:flex lg:w-80">
              <InvoicesTabSideNav context={CreateInvoiceContext} />
            </div>

            <div
              id="scrollable div"
              className="customNormalScroll flex grow flex-col space-y-8 overflow-y-auto p-12 pl-0">
              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.BILL_TO}>
                <CreateInvoiceCustomer context={CreateInvoiceContext} />
              </div>

              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.INVOICE_OPTIONS}>
                <InvoiceOptionsSection context={CreateInvoiceContext} />
              </div>

              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.LINE_ITEMS}>
                <LineItemsSection isLoadingTaxesAndFees={isLoadingTaxesAndFees} context={CreateInvoiceContext} />
              </div>

              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.NOTES_ATTACHMENTS}>
                <NotesAttachmentsSection
                  context={CreateInvoiceContext}
                  isLoadingTaxesAndFees={isLoadingTaxesAndFees}
                  taxesAndFees={taxesAndFeesData}
                />
              </div>

              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.PAYMENT_OPTIONS}>
                <PaymentOptionsSection taxesAndFees={taxesAndFeesData} context={CreateInvoiceContext} />
              </div>
              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.SIGNATURES}>
                <SignatureOptionsSection
                  updateSignatureState={handleSignatureStateChange}
                  signatureState={selectedIsSignatureEnabled}
                  context={CreateInvoiceContext}
                  isDisabled={checkIsSignatureDisabled()}
                  id="create-invoice-allow-sign-toggle-switch"
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {showSyncConfirmationPopup && (
        <GenericModal
          showModal={true}
          title={getSyncPopupTitleAndSubtitle('title')}
          titleStyle="text-center"
          subTitleStyle="text-center text-primaryText text-sbase font-normal"
          subTitle={getSyncPopupTitleAndSubtitle('subtitle')}
          primaryBtnLabel="Yes, continue"
          primaryBtnId="sync-job-invoice"
          primaryBtnStyle="mt-2"
          onPrimaryBtnClick={handleCreateInvoiceFromOtherEntities}
          secondaryBtnId="cancel-sync-job-invoice"
          secondaryBtnStyle="border-none shadow-none hover:bg-transparent"
          secondaryBtnLabel="Cancel"
          onSecondaryBtnClick={() => setShowSyncConfirmationPopup(false)}
          closeIconStyle="top-3 right-3"
          CloseIcon={() => (
            <CrossIconvector
              height={18}
              width={18}
              title="sync-popup-close"
              onClick={() => setShowSyncConfirmationPopup(false)}
              className="cursor-pointer"
            />
          )}
          isActionInProgress={isCreateInvoiceFromJobLoading}
          height="min-h-[262px]"
          className="!px-5 !py-6"
        />
      )}
      {showNoSelfCheckoutPopup && (
        <NoSelfCheckoutPopup
          isEdit={false}
          onContinue={handleOnCreateInvoiceWithoutSelfCheckout}
          onEditPaymentMethods={handleOnEditPaymentMethods}
          onClose={handleCloseNoSelfCheckoutPopup}
          isActionLoading={isCreateInvoiceLoading}
        />
      )}
      {showInvoiceErrorsPopup.open && (
        <InvoiceErrorsPopup
          onClose={handleCloseInvoiceErrorsPopup}
          errorFields={showInvoiceErrorsPopup.errors}
          maximumAmount={+currentTeam?.maximumAmount}
          minimumAmount={+currentTeam?.minimumAmount}
          onContinue={handleCloseInvoiceErrorsPopup}
        />
      )}
      {showMerchantBlockedPopup && (
        <BlockedInfoPopup isOpen={showMerchantBlockedPopup} onClose={() => setShowMerchantBlockedPopup(false)} />
      )}
    </CreateInvoiceContext.Provider>
  );
};

export default CreateInvoice;
